import { jsx, jsxs } from "react/jsx-runtime";
import { useRef, useState, useMemo, useEffect } from "react";
import { formatMoney } from "../../utils/stringUtils.js";
import { getEntityImage } from "../../utils/assetUtils.js";
import { getAppConsistentParams } from "../../utils/appUtils.js";
import { getProductUrl } from "../../utils/productUtils.js";
import { VariantPickerWidget } from "@brandclub/bc-widgets";
import { MOBILE_SCREEN_SIZE, MOBILE_HORIZONTAL_PADDING, TABLET_SCREEN_SIZE, TABLET_HORIZONTAL_PADDING } from "../../utils/constants/Constants.js";
const getFontWeight = (weight) => {
  const weightMap = {
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700
  };
  return weightMap[weight] || 500;
};
const VisuallyHidden = ({ children }) => /* @__PURE__ */ jsx(
  "span",
  {
    style: {
      position: "absolute",
      width: 1,
      height: 1,
      padding: 0,
      margin: -1,
      overflow: "hidden",
      clip: "rect(0,0,0,0)",
      whiteSpace: "nowrap",
      border: 0
    },
    children
  }
);
const getVariantDisplayName = (variant) => {
  const values = variant.variationNameValues;
  if (values && Object.keys(values).length > 0) {
    const priorityKeys = ["sizeName", "colorName", "styleName", "patternName"];
    for (const key of priorityKeys) {
      if (values[key]) return values[key];
    }
    const firstValue = Object.values(values)[0];
    if (firstValue) return firstValue;
  }
  return variant.title || "Variant";
};
const getVariantImage = (variant) => {
  if (variant.imageUrls && variant.imageUrls.length > 0) {
    return variant.imageUrls[0];
  }
  return getEntityImage(variant.stacklineSku, "product");
};
const sortVariants = (variants) => {
  return [...variants].sort((a, b) => {
    const nameA = getVariantDisplayName(a).toLowerCase();
    const nameB = getVariantDisplayName(b).toLowerCase();
    const nameCompare = nameA.localeCompare(nameB);
    if (nameCompare !== 0) return nameCompare;
    return a.stacklineSku.localeCompare(b.stacklineSku);
  });
};
const RectangleVariantCard = ({ variant, isSelected, onClick, state, resolveColor }) => {
  const [isHovered, setIsHovered] = useState(false);
  const displayName = getVariantDisplayName(variant);
  const fullTitle = variant.title || displayName;
  const imageUrl = getVariantImage(variant);
  const price = variant.retailPrice;
  const priceText = price !== void 0 ? formatMoney(price) : "";
  return /* @__PURE__ */ jsxs(
    "button",
    {
      type: "button",
      onClick,
      onMouseEnter: () => setIsHovered(true),
      onMouseLeave: () => setIsHovered(false),
      title: fullTitle,
      "aria-pressed": isSelected,
      "aria-label": `${fullTitle}${priceText ? `, ${priceText}` : ""}${isSelected ? ", currently selected" : ""}`,
      style: {
        position: "relative",
        display: "flex",
        flexDirection: "column",
        borderRadius: state.borderRadius,
        backgroundColor: isHovered && !isSelected ? resolveColor(state.backgroundColorHover) : resolveColor(state.backgroundColor),
        width: state.cardWidth,
        flexShrink: 0,
        cursor: "pointer",
        transition: "all 0.2s",
        overflow: "visible",
        border: `1px solid ${isSelected ? resolveColor(state.borderColorSelected) : resolveColor(state.borderColor)}`,
        padding: 0
      },
      children: [
        isSelected && /* @__PURE__ */ jsxs(
          "svg",
          {
            width: state.checkmarkSize,
            height: state.checkmarkSize,
            viewBox: "0 0 16 16",
            fill: "none",
            xmlns: "http://www.w3.org/2000/svg",
            style: { position: "absolute", top: -8, right: -8, zIndex: 1 },
            "aria-hidden": "true",
            children: [
              /* @__PURE__ */ jsx("circle", { cx: "8", cy: "8", r: "8", fill: resolveColor(state.checkmarkBgColor) }),
              /* @__PURE__ */ jsx(
                "path",
                {
                  d: "M4.5 8L7 10.5L11.5 6",
                  stroke: resolveColor(state.checkmarkIconColor),
                  strokeWidth: "1.5",
                  strokeLinecap: "round",
                  strokeLinejoin: "round"
                }
              )
            ]
          }
        ),
        isSelected && /* @__PURE__ */ jsx(VisuallyHidden, { children: "Selected:" }),
        /* @__PURE__ */ jsx("div", { style: { display: "flex", alignItems: "center", justifyContent: "center", padding: 8, paddingBottom: 8 }, children: /* @__PURE__ */ jsx(
          "div",
          {
            style: {
              width: state.cardImageSize,
              height: state.cardImageSize,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0
            },
            children: /* @__PURE__ */ jsx(
              "img",
              {
                src: imageUrl,
                alt: "",
                "aria-hidden": "true",
                style: { maxWidth: "100%", maxHeight: "100%", objectFit: "contain", borderRadius: 4 }
              }
            )
          }
        ) }),
        /* @__PURE__ */ jsx(
          "div",
          {
            style: {
              width: "100%",
              height: 1,
              backgroundColor: isSelected ? resolveColor(state.dividerColorSelected) : resolveColor(state.dividerColor)
            }
          }
        ),
        /* @__PURE__ */ jsxs("div", { style: { display: "flex", flexDirection: "column", alignItems: "flex-start", padding: 8 }, children: [
          price !== void 0 && /* @__PURE__ */ jsx(
            "span",
            {
              style: {
                fontSize: state.priceFontSize,
                color: resolveColor(state.priceColor),
                lineHeight: "20px",
                fontWeight: getFontWeight(isSelected ? state.priceFontWeightSelected : state.priceFontWeightNormal)
              },
              "aria-hidden": "true",
              children: priceText
            }
          ),
          /* @__PURE__ */ jsx(
            "span",
            {
              style: {
                fontSize: state.nameFontSize,
                color: resolveColor(state.nameColor),
                lineHeight: "20px",
                textAlign: "left",
                width: "100%",
                fontWeight: getFontWeight(isSelected ? state.nameFontWeightSelected : state.nameFontWeightNormal),
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden"
              },
              "aria-hidden": "true",
              children: displayName
            }
          )
        ] })
      ]
    }
  );
};
const CompactVariantPill = ({ variant, isSelected, onClick, state, resolveColor }) => {
  const [isHovered, setIsHovered] = useState(false);
  const displayName = getVariantDisplayName(variant);
  const fullTitle = variant.title || displayName;
  const imageUrl = getVariantImage(variant);
  return /* @__PURE__ */ jsxs(
    "button",
    {
      type: "button",
      onClick,
      onMouseEnter: () => setIsHovered(true),
      onMouseLeave: () => setIsHovered(false),
      title: fullTitle,
      "aria-pressed": isSelected,
      "aria-label": `${fullTitle}${isSelected ? ", currently selected" : ""}`,
      style: {
        display: "flex",
        alignItems: "center",
        gap: state.pillGap,
        borderRadius: state.borderRadius,
        backgroundColor: isHovered && !isSelected ? resolveColor(state.backgroundColorHover) : resolveColor(state.backgroundColor),
        padding: `${state.pillPaddingY}px ${state.pillPaddingX}px`,
        paddingLeft: state.pillPaddingY,
        // Less padding on left for image
        cursor: "pointer",
        transition: "all 0.2s",
        maxWidth: 200,
        border: `1px solid ${isSelected ? resolveColor(state.borderColorSelected) : resolveColor(state.borderColor)}`
      },
      children: [
        isSelected && /* @__PURE__ */ jsx(VisuallyHidden, { children: "Selected:" }),
        /* @__PURE__ */ jsx(
          "div",
          {
            style: {
              width: state.pillImageSize,
              height: state.pillImageSize,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0
            },
            children: /* @__PURE__ */ jsx(
              "img",
              {
                src: imageUrl,
                alt: "",
                "aria-hidden": "true",
                style: { maxWidth: "100%", maxHeight: "100%", objectFit: "contain", borderRadius: 4 }
              }
            )
          }
        ),
        /* @__PURE__ */ jsx(
          "span",
          {
            style: {
              fontSize: state.nameFontSizeCompact,
              color: resolveColor(state.nameColor),
              fontWeight: getFontWeight(isSelected ? state.nameFontWeightSelected : state.nameFontWeightNormal),
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap"
            },
            "aria-hidden": "true",
            children: displayName
          }
        )
      ]
    }
  );
};
const ProductVariantPicker = ({
  currentStacklineSku,
  variants,
  onVariantSelect,
  navigate
}) => {
  const containerRef = useRef(null);
  const [useCompactLayout, setUseCompactLayout] = useState(false);
  const [showAllVariants, setShowAllVariants] = useState(false);
  const sortedVariants = useMemo(() => sortVariants(variants), [variants]);
  const shouldShowToggle = sortedVariants.length > 10;
  const variantsToShow = shouldShowToggle && !showAllVariants ? sortedVariants.slice(0, 10) : sortedVariants;
  if (!variants || variants.length <= 1) {
    return null;
  }
  const handleVariantClick = (variant) => {
    const { stacklineSku } = variant;
    if (stacklineSku === currentStacklineSku) return;
    if (onVariantSelect) {
      onVariantSelect(stacklineSku);
      return;
    }
    if (navigate) {
      const urlSearchParams = getAppConsistentParams(window.location.search);
      const targetUrl = getProductUrl({
        title: variant.title,
        stacklineSku: variant.stacklineSku,
        urlSearchParams
      });
      navigate({
        pathname: targetUrl.split("?")[0],
        search: targetUrl.split("?")[1]
      });
    }
  };
  return /* @__PURE__ */ jsx(VariantPickerWidget, { widgetId: "product-variant-picker", displayName: "Variant Picker", children: (state, resolveColor) => /* @__PURE__ */ jsx(
    ProductVariantPickerInner,
    {
      containerRef,
      useCompactLayout,
      setUseCompactLayout,
      showAllVariants,
      setShowAllVariants,
      sortedVariants,
      shouldShowToggle,
      variantsToShow,
      currentStacklineSku,
      handleVariantClick,
      state,
      resolveColor
    }
  ) });
};
const ProductVariantPickerInner = ({
  containerRef,
  useCompactLayout,
  setUseCompactLayout,
  showAllVariants,
  setShowAllVariants,
  sortedVariants,
  shouldShowToggle,
  variantsToShow,
  currentStacklineSku,
  handleVariantClick,
  state,
  resolveColor
}) => {
  const [paddingLeft, setPaddingLeft] = useState(0);
  useEffect(() => {
    if (!containerRef.current) return;
    const checkLayout = () => {
      var _a;
      const containerWidth = ((_a = containerRef.current) == null ? void 0 : _a.offsetWidth) || 0;
      const totalCardsWidth = sortedVariants.length * state.cardWidth + (sortedVariants.length - 1) * state.cardGap;
      setUseCompactLayout(totalCardsWidth > containerWidth);
    };
    checkLayout();
    const resizeObserver = new ResizeObserver(checkLayout);
    resizeObserver.observe(containerRef.current);
    return () => resizeObserver.disconnect();
  }, [sortedVariants.length, state.cardWidth, state.cardGap, containerRef, setUseCompactLayout]);
  useEffect(() => {
    const updatePadding = () => {
      const width = window.innerWidth;
      if (width <= MOBILE_SCREEN_SIZE) {
        setPaddingLeft(MOBILE_HORIZONTAL_PADDING);
      } else if (width <= TABLET_SCREEN_SIZE) {
        setPaddingLeft(TABLET_HORIZONTAL_PADDING);
      } else {
        setPaddingLeft(0);
      }
    };
    updatePadding();
    window.addEventListener("resize", updatePadding);
    return () => window.removeEventListener("resize", updatePadding);
  }, []);
  return /* @__PURE__ */ jsxs(
    "div",
    {
      ref: containerRef,
      id: "product-variant-picker",
      style: {
        width: "100%",
        marginBottom: 20,
        paddingLeft: paddingLeft > 0 ? paddingLeft : void 0
      },
      children: [
        /* @__PURE__ */ jsx(
          "div",
          {
            role: "group",
            "aria-label": `Product variants, ${sortedVariants.length} options available`,
            style: {
              display: "flex",
              flexWrap: "wrap",
              alignItems: "stretch",
              paddingRight: 8,
              gap: useCompactLayout ? state.pillGap : state.cardGap
            },
            children: variantsToShow.map((variant) => {
              const isSelected = variant.stacklineSku === currentStacklineSku;
              if (useCompactLayout) {
                return /* @__PURE__ */ jsx(
                  CompactVariantPill,
                  {
                    variant,
                    isSelected,
                    onClick: () => handleVariantClick(variant),
                    state,
                    resolveColor
                  },
                  variant.stacklineSku
                );
              }
              return /* @__PURE__ */ jsx(
                RectangleVariantCard,
                {
                  variant,
                  isSelected,
                  onClick: () => handleVariantClick(variant),
                  state,
                  resolveColor
                },
                variant.stacklineSku
              );
            })
          }
        ),
        shouldShowToggle && /* @__PURE__ */ jsx(
          "button",
          {
            type: "button",
            onClick: () => setShowAllVariants(!showAllVariants),
            style: {
              marginTop: 12,
              fontSize: 14,
              fontWeight: 500,
              color: resolveColor(state.nameColor),
              cursor: "pointer",
              background: "none",
              border: "none",
              padding: 0,
              textDecoration: "none"
            },
            "aria-expanded": showAllVariants,
            children: showAllVariants ? "View less" : "View more"
          }
        )
      ]
    }
  );
};
export {
  ProductVariantPicker
};
//# sourceMappingURL=ProductVariantPicker.js.map
